import { Card, CardHeader, CardBody, Badge, ScoreBar } from '@neurograph/primitives';
import { useStudio } from '../providers/StudioContext';

export function ReviewPanel() {
  const { review } = useStudio();

  if (!review) {
    return (
      <div className="text-center py-16 px-8">
        <h3 className="text-lg font-semibold text-neutral-700 mb-2">No Review Available</h3>
        <p className="text-neutral-500 text-sm max-w-sm mx-auto">
          Ask to review a creative asset through this persona&apos;s lens to see a cognitive
          walkthrough with scores, recommendations, and moment-by-moment reactions.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-neutral-900">Creative Review</h2>
        {review.summary?.verdict && (
          <Badge variant={review.summary.verdict === 'strong' ? 'success' : 'warning'}>
            {review.summary.verdict}
          </Badge>
        )}
      </div>

      {/* Summary */}
      {review.summary && (
        <Card>
          <CardHeader>
            <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Summary
            </span>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-neutral-700 leading-relaxed mb-3">
              {review.summary.one_line}
            </p>
            {review.summary.overall_score !== undefined && (
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-3xl font-bold text-primary-600">
                  {review.summary.overall_score}
                </span>
                <span className="text-sm text-neutral-400">/100</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {review.summary.strengths?.map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-green-500 shrink-0">✓</span>
                  <span className="text-neutral-600">{s}</span>
                </div>
              ))}
              {review.summary.gaps?.map((g, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-amber-500 shrink-0">△</span>
                  <span className="text-neutral-600">{g}</span>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Scores */}
      {review.scores && Object.keys(review.scores).length > 0 && (
        <Card>
          <CardHeader>
            <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Dimension Scores
            </span>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              {Object.entries(review.scores).map(([key, value]) => (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-neutral-500 min-w-[140px] capitalize">
                    {key.replace(/_/g, ' ')}
                  </span>
                  <div className="flex-1">
                    <ScoreBar score={value} max={100} showValue={false} />
                  </div>
                  <span className="text-sm font-semibold text-neutral-700 min-w-[30px] text-right">
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Recommendations */}
      {review.recommendations && review.recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Recommendations
            </span>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              {review.recommendations.map((rec, i) => (
                <div key={i} className="py-2 border-b border-neutral-100 last:border-0">
                  <p className="text-sm font-semibold text-neutral-800 mb-1">
                    {rec.title || rec.recommendation}
                  </p>
                  {rec.description && (
                    <p className="text-sm text-neutral-500 leading-relaxed">
                      {rec.description}
                    </p>
                  )}
                  {rec.priority && (
                    <Badge variant={rec.priority === 'high' ? 'warning' : 'neutral'} className="mt-1">
                      {rec.priority}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
